import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

export function createStudio(canvas) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.92;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#0b0c0f');
  scene.fog = new THREE.Fog('#0b0c0f', 1.8, 6.5);

  const camera = new THREE.PerspectiveCamera(32, window.innerWidth / window.innerHeight, 0.02, 20);
  camera.position.set(0.72, 0.36, 1.08);

  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.32;

  const hemi = new THREE.HemisphereLight('#c8d4e4', '#2a1c12', 0.35);
  scene.add(hemi);

  const key = new THREE.SpotLight('#ffd8b0', 11, 8, 0.5, 0.48, 1.3);
  key.position.set(0.85, 1.35, 1.05);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.bias = -0.00015;
  key.shadow.normalBias = 0.02;
  scene.add(key);
  scene.add(key.target);
  key.target.position.set(0, 0.22, 0);

  const fill = new THREE.SpotLight('#a8c4e8', 4.2, 8, 0.72, 0.6, 1.2);
  fill.position.set(-0.95, 0.7, 0.7);
  scene.add(fill);

  const rim = new THREE.DirectionalLight('#ffe8d0', 1.15);
  rim.position.set(-0.55, 0.9, -1.0);
  scene.add(rim);

  const kick = new THREE.PointLight('#d26432', 0.45, 1.6, 2);
  kick.position.set(0.18, 0.14, 0.28);
  scene.add(kick);

  const ground = new THREE.Mesh(new THREE.CircleGeometry(2.4, 64), new THREE.MeshStandardMaterial({
    color: '#121114',
    roughness: 0.92,
    metalness: 0.04,
  }));
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.06;
  controls.minDistance = 0.28;
  controls.maxDistance = 2.4;
  controls.maxPolarAngle = Math.PI * 0.49;
  controls.target.set(0.02, 0.3, 0);
  controls.update();

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.08, 0.35, 0.93);
  composer.addPass(bloom);
  const pr = renderer.getPixelRatio();
  composer.addPass(new SMAAPass(window.innerWidth * pr, window.innerHeight * pr));
  composer.addPass(new OutputPass());

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    composer.setSize(w, h);
  }
  window.addEventListener('resize', resize);

  return { renderer, scene, camera, controls, composer, reduced };
}
